import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LayoutContext = createContext(null);

// Largura a partir da qual a sidebar deixa de ser fixa e vira um drawer.
// Mantém-se em sincronia com o breakpoint usado nos CSS (Sidebar.css / MainLayout.css).
const MOBILE_BREAKPOINT = 768;

export const LayoutProvider = ({ children }) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // MainLayout sinaliza que há uma sidebar na árvore atual; o Navbar usa isso
  // para só exibir o hambúrguer nas rotas que realmente têm menu lateral.
  const [sidebarMounted, setSidebarMounted] = useState(false);

  const openMobileSidebar = useCallback(() => setMobileSidebarOpen(true), []);
  const closeMobileSidebar = useCallback(() => setMobileSidebarOpen(false), []);
  const toggleMobileSidebar = useCallback(() => setMobileSidebarOpen((open) => !open), []);

  // Trava a rolagem do body enquanto o drawer está aberto.
  useEffect(() => {
    if (!mobileSidebarOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileSidebarOpen]);

  // Fecha o drawer ao pressionar Esc.
  useEffect(() => {
    if (!mobileSidebarOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMobileSidebarOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileSidebarOpen]);

  // Ao voltar para uma largura de desktop (ex.: rotação/redimensionamento),
  // garante que o drawer não fique preso aberto.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > MOBILE_BREAKPOINT) setMobileSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const value = useMemo(
    () => ({
      mobileSidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      toggleMobileSidebar,
      sidebarMounted,
      setSidebarMounted,
    }),
    [mobileSidebarOpen, openMobileSidebar, closeMobileSidebar, toggleMobileSidebar, sidebarMounted]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout deve ser usado dentro de LayoutProvider');
  }
  return context;
};
